/* eslint-disable react-hooks/exhaustive-deps */
import React, { useLayoutEffect, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import map from 'lodash/map';
import find from 'lodash/find';
import filter from 'lodash/filter';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import { NavItem, Nav } from 'reactstrap';
import firebase from '../../config/firebase.config';
import BlockUi from 'react-block-ui';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import styles from './Sidebar.module.scss';
import classNames from 'classnames';
import {
  managementItems,
  teamReportItems,
  sidebarSections,
} from './Sidebar.constants';
import { SidebarNavRow } from './components/SidebarNavRow.component';
import { SidebarSection } from './components/SidebarSection.component';
import { Text, fontWeightEnum, textVariantsEnum } from '@/components/Text';
import { useFeatures } from '@/hooks/useFeatures';
import { useActiveCompanyRole } from '@/hooks/usePermission';
import { Button, buttonVariants } from '@/components/Button';
import { TooltipWrapper } from '@/components/TooltipWrapper';
import { authActionTypes } from '@store/Auth/Auth.actionTypes';
import { MdLogout } from 'react-icons/md';
import DefaultAvatar from '@assets/img/svg/avatar.svg';
import { routesData, isRoleAuthorizedForRoute } from '@/routes/routesData';

const database = firebase.firestore();

// Keep in sync with STAFF_UIDS in functions/staff-auth.js.
const STAFF_UIDS = [
  'tBSIKtrev2UYKWz3bgk4VQlbWie2', // ayush@prodaff.com
  'aMep0ZI5tBcyDYlJI6tb1OqxLBw2', // ayush@wfhgo.com
];

// Section rendering order is driven entirely by Sidebar.constants' config —
// add/reorder a section there and this list (and the nav) follows.
const SECTION_ORDER = Object.keys(sidebarSections);

const CustomNavItem = ({ closeCollapse, isCollapsed }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [unreadAlertsCount, setUnreadAlertsCount] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const { hasAccess } = useFeatures();
  const teamReducer = useSelector((state) => ({
    loading: false,
    activeTeam: state.team.activeTeam,
    activeUserID: state.team.activeUserID,
    members: state.team.teamMembers,
    teams: state.team.teams,
    all: state.team.all,
    timelogDayRange: state.team.timelogDayRange,
    start: state.start,
    end: state.end,
    week: state.team.week,
  }));

  const userProfile = useSelector((state) => state.auth.profile);
  const activeCompanyRole = useActiveCompanyRole();
  const routeReducer = useSelector((state) => ({
    location: state?.router?.location,
  }));

  // routesData is a static, module-level array — this map never changes
  // across renders, so it's built once. Looked up by navLink so sidebar
  // visibility is derived from the exact same authorizedFor data that
  // gates direct URL access, instead of a separate hand-maintained set of
  // role checks that can drift out of sync with it.
  const pathToRoute = useMemo(
    () => Object.fromEntries(routesData.map((route) => [route.path, route])),
    []
  );

  useLayoutEffect(() => {
    // Prefer the last-picked department (localStorage 'teamData') over the
    // backend's persisted active team, which can be stale.
    let storedActiveTeamId;
    try {
      storedActiveTeamId = JSON.parse(
        localStorage.getItem('teamData') || 'null'
      )?.activeTeamid;
    } catch (_) {
      storedActiveTeamId = undefined;
    }

    dispatch({
      type: teamActionTypes.GET_USER_ACTIVE_TEAM,
      payload: {
        userId: userProfile?.id,
        companyId: !_.isEmpty(userProfile?.activeCompany)
          ? userProfile?.activeCompany?.companyId
          : '',
        role: !_.isEmpty(userProfile?.role) ? userProfile?.role : '',
        days: teamReducer.timelogDayRange,
        start: teamReducer.start,
        end: teamReducer.end,
        activeTeamid: storedActiveTeamId,
      },
    });
  }, [userProfile]);

  useEffect(() => {
    if (teamReducer.activeTeam.activeTeamid !== undefined) {
      dispatch({
        type: teamActionTypes.SET_TODAY_TIMES,
        payload: {
          selectedTeamID: teamReducer.activeTeam.activeTeamid,
          day: teamReducer.timelogDayRange,
        },
      });
      dispatch({
        type: teamActionTypes.SET_TRENDS_DATA,
        payload: {
          selectedTeamID: teamReducer.activeTeam.activeTeamid,
          week: teamReducer.week,
        },
      });
      // dispatch({
      //   type: "team/SET_TOP_USERS",
      //   payload: {
      //     selectedTeamID: teamReducer.activeTeam.activeTeamid,
      //     day: teamReducer.timelogDayRange,
      //   },
      // });
    }
  }, [teamReducer.activeTeam.activeTeamid]);

  let members = [];
  if (!isEmpty(teamReducer.members)) {
    const activeMember = find(
      teamReducer.members,
      (value) => value.userUID === userProfile?.id
    );
    if (activeMember) {
      if (activeMember.userType === 'Manager') {
        const sortMembers = filter(
          teamReducer.members,
          (value) => value.userUID !== userProfile?.id
        );
        const currentMember = find(
          teamReducer.members,
          (value) => value.userUID === userProfile?.id
        );
        members = [...sortMembers, currentMember];
      } else {
        members = filter(
          teamReducer.members,
          (value) => value.userUID === userProfile?.id
        );
      }
    }
  }

  function handleRouting(navItem) {
    if (navItem.external && navItem.navLink) {
      window.open(navItem.navLink, '_blank', 'noopener,noreferrer');
      closeCollapse();
      return;
    }
    let navUrl = navItem.navLink;
    // if (navItem.id === "team-dashboard") {
    //   const companyId = !_.isEmpty(userProfile?.activeCompany)
    //     ? userProfile?.activeCompany?.companyId
    //     : "";
    //   navUrl += `?cid=${companyId}`;
    // }
    history.push(navUrl);
    closeCollapse();
  }

  // Check if user has an active company
  const hasActiveCompany = !isEmpty(userProfile?.activeCompany?.companyId);

  // Check if user profile is loaded
  const isProfileLoaded = userProfile && userProfile.id;

  // Check if user has companies but no active company
  const hasCompaniesButNoActive =
    userProfile?.companyList &&
    userProfile.companyList.length > 0 &&
    !hasActiveCompany;

  // Filter navItems based on company dependency
  let filteredTeamReportItems = teamReportItems;
  let filteredManagementItems = managementItems;
  filteredTeamReportItems = filteredTeamReportItems.filter((item) => {
    if (item.moduleId) {
      return hasAccess.module(item.moduleId);
    }
    return true;
  });
  filteredManagementItems = filteredManagementItems.filter((item) => {
    if (item.moduleId) {
      return hasAccess.module(item.moduleId);
    }
    return true;
  });
  if (!isProfileLoaded) {
    filteredTeamReportItems = teamReportItems;
    filteredManagementItems = managementItems;
    // Show loading state - keep all items visible while loading
  } else if (!hasActiveCompany) {
    // When no active company, show only invitations
    filteredTeamReportItems = filteredTeamReportItems.filter(
      (item) =>
        item.id === 'invitations-hub' ||
        item.id === 'download-desktop-app' ||
        item.id === 'support-portal'
    );
    filteredManagementItems = filteredManagementItems.filter(
      (item) =>
        item.id === 'invitations-hub' ||
        item.id === 'download-desktop-app' ||
        item.id === 'support-portal'
    );
  } else {
    // Role-based visibility is derived from the exact same authorizedFor
    // data that gates direct URL access in routesData.jsx — a route's
    // allowed roles are edited in one place (routesData.jsx) and both page
    // access and sidebar visibility update together. This also naturally
    // reproduces the old Client allow-list: Client only matches routes
    // whose authorizedFor explicitly includes 'Client'.
    const isItemAuthorized = (item) => {
      if (item.external) return true;
      const route = pathToRoute[item.navLink];
      if (!route) return true; // no matching route entry — fail open, don't hide
      return isRoleAuthorizedForRoute(route, activeCompanyRole);
    };
    filteredTeamReportItems = filteredTeamReportItems.filter(isItemAuthorized);
    filteredManagementItems = filteredManagementItems.filter(isItemAuthorized);
  }
  // Staff-only nav item — hidden for everyone except this hardcoded uid.
  // Applied last, unconditionally, so the loading-state reset above can't
  // let it flash for other users. Keep in sync with STAFF_UIDS in
  // functions/staff-auth.js.
  if (!STAFF_UIDS.includes(userProfile?.id)) {
    filteredManagementItems = filteredManagementItems.filter(
      (item) => item.id !== 'app-version-publish'
    );
  }
  // Listen to unread alerts count
  // Alerts are stored in membersDatabase/{userId}/alerts subcollection
  useEffect(() => {
    const userId = userProfile?.id;
    if (!userId) {
      setUnreadAlertsCount(0);
      return;
    }

    const unsubscribe = database
      .collection('membersDatabase')
      .doc(userId)
      .collection('alerts')
      .where('isRead', '==', false)
      .onSnapshot(
        (snapshot) => {
          setUnreadAlertsCount(snapshot.size);
        },
        (error) => {
          console.error('Error listening to unread alerts count:', error);
          setUnreadAlertsCount(0);
        }
      );
    const unsubscribeNotifications = database
      .collection('membersDatabase')
      .doc(userId)
      .collection('notifications')
      .where('isRead', '==', false)
      .onSnapshot(
        (snapshot) => {
          setUnreadNotificationsCount(snapshot.size);
        },
        (error) => {
          console.error(
            'Error listening to unread notifications count:',
            error
          );
          setUnreadNotificationsCount(0);
        }
      );
    return () => {
      unsubscribe();
      unsubscribeNotifications();
    };
  }, [userProfile?.id]);
  const getIsNavItemActive = (navItem) => {
    if (navItem.external) return false;
    return routeReducer.location.pathname === navItem.navLink;
  };
  const getBadgeCount = (navItem) => {
    if (navItem.id === 'alerts') return unreadAlertsCount;
    if (navItem.id === 'notifications') return unreadNotificationsCount;
    return 0;
  };

  // All access-controlled items, grouped by their display section. Access
  // control itself already happened above on the two flat arrays — this only
  // decides which labeled group a surviving item renders under.
  const itemsBySection = groupBy(
    [...filteredTeamReportItems, ...filteredManagementItems],
    'section'
  );

  // Section eyebrow labels (and the mobile-degraded "no active company" view)
  // stay hidden together with icon-only collapse — both are "less chrome"
  // states that only make sense without the label text.
  const hideLabels = isCollapsed || !hasActiveCompany;

  return (
    <BlockUi
      tag="div"
      blocking={teamReducer.loading}
      className={styles.navItemsScrollArea}
    >
      <Nav navbar>
        {/* Show helpful message when no active company */}
        {!isCollapsed && isProfileLoaded && !hasActiveCompany && (
          <NavItem>
            <div
              className="px-3 py-3 text-center border border-primary rounded mx-2 mb-2"
              style={{ backgroundColor: '#e3f2fd' }}
            >
              <small className="text-muted d-block mb-2">
                <i className="fas fa-info-circle mr-1 text-primary"></i>
                {hasCompaniesButNoActive
                  ? 'Select a company to access department features'
                  : 'Create a company to access department features'}
              </small>
              <div className="d-flex flex-column gap-2">
                {hasCompaniesButNoActive ? (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      history.push('/select-company');
                      closeCollapse();
                    }}
                  >
                    Select Company
                  </button>
                ) : (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      history.push('/add-company');
                      closeCollapse();
                    }}
                  >
                    Create Company
                  </button>
                )}
              </div>
            </div>
          </NavItem>
        )}
        <div className={classNames(styles.navItemsContainer, 'with-scroll')}>
          {SECTION_ORDER.map((sectionKey) => {
            const sectionItems = itemsBySection[sectionKey] || [];

            return (
              <SidebarSection
                key={sectionKey}
                label={sidebarSections[sectionKey]}
                collapsed={hideLabels}
              >
                {map(sectionItems, (navItem) => (
                  <SidebarNavRow
                    key={navItem.id}
                    itemId={navItem.id}
                    icon={navItem.icon}
                    label={navItem.title}
                    active={getIsNavItemActive(navItem)}
                    badgeCount={getBadgeCount(navItem)}
                    collapsed={isCollapsed}
                    onClick={() => handleRouting(navItem)}
                  />
                ))}
              </SidebarSection>
            );
          })}
        </div>
        <div className={styles.navItemsContainerFooter}>
          {isProfileLoaded && (
            <button
              type="button"
              className={styles.profileCard}
              disabled={activeCompanyRole === 'Client'}
              onClick={() => {
                // Client is dashboard-only and has no profile/settings page.
                if (activeCompanyRole === 'Client') return;
                history.push('/profile');
                closeCollapse();
              }}
            >
              <img
                src={
                  !isEmpty(userProfile?.avatar)
                    ? userProfile.avatar
                    : DefaultAvatar
                }
                alt={userProfile?.name}
                className={styles.profileAvatar}
              />
              {!isCollapsed && (
                <span className={styles.profileMeta}>
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Semibold}
                    className={styles.profileName}
                  >
                    {userProfile.name}
                  </Text>
                  <Text
                    variant={textVariantsEnum.Text_xs}
                    fontWeight={fontWeightEnum.Normal}
                    color="gray-500"
                  >
                    {activeCompanyRole || 'Member'}
                  </Text>
                </span>
              )}
            </button>
          )}
          {isCollapsed ? (
            <TooltipWrapper tooltip="Log Out" tooltipPlacement="right">
              <Button
                variant={buttonVariants.ghost_danger}
                icon={<MdLogout size={18} color="var(--error-600)" />}
                className={styles.logoutButton}
                onClick={() => {
                  dispatch({
                    type: authActionTypes.LOGOUT_ACTIVE_ACCOUNT,
                  });
                }}
              />
            </TooltipWrapper>
          ) : (
            <Button
              text="Log Out"
              variant={buttonVariants.ghost_danger}
              icon={<MdLogout size={18} color="var(--error-600)" />}
              className={styles.logoutButton}
              onClick={() => {
                dispatch({
                  type: authActionTypes.LOGOUT_ACTIVE_ACCOUNT,
                });
              }}
            />
          )}
        </div>
      </Nav>
    </BlockUi>
  );
};

CustomNavItem.propTypes = {};

export default CustomNavItem;
